import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_SYSTEM_BACKUP_API,
} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	isRecentQuery,
	LOOKUP_FALLBACK_LIMIT,
	LOOKUP_LIMIT,
	mostRecentRow,
	rankLookupCandidates,
	responseRows,
} from '../../core/resolutionUtils';
import {resolveByFields} from '../../core/fieldSearch';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return a System Backup row identifier.
 * @param {object} item Canvas system backup row
 * @return {*} identifier
 */
function systemBackupId(item) {
	return item?.backup_id ?? item?.id;
}

/**
 * Normalize a Canvas System Backup row.
 *
 * System Backup has no Site column - unlike Site Backup, `site_name` is
 * always null here.
 *
 * @param {object} item Canvas system backup row
 * @return {object|null} agent candidate
 */
function normalizeSystemBackup(item) {
	const id = systemBackupId(item);
	const name = item?.backup_name;
	if (id == null || !name) return null;
	return {
		entity_type: 'system_backup',
		entity_id: String(id),
		name: String(name),
		site_id: null,
		site_name: null,
		serial_no: null,
	};
}

/**
 * Load System Backup rows, paging until the lookup cap.
 * @param {string|null} search name search
 * @param {number} limit maximum rows
 * @return {Promise<Array>} rows
 */
async function loadRows(search, limit) {
	const rows = [];
	const pageSize = Math.min(Math.max(limit, 1), LOOKUP_LIMIT);
	let offset = 0;
	while (rows.length < limit) {
		const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
		const url = `${ENDPOINT_URL}${GET_SYSTEM_BACKUP_API}` +
			`?limit=${pageSize}&offset=${offset}${searchParam}`;
		const {data} = await axios.get(url);
		const page = responseRows(decodeAgentResponse(data) || {});
		rows.push(...page);
		if (page.length < pageSize) break;
		offset += pageSize;
	}
	return rows.slice(0, limit);
}

/**
 * Rank loaded rows through the catalogue the agent sent.
 * @param {Array} rows Canvas rows
 * @param {object} params resolver parameters
 * @param {string} query lookup query
 * @param {number} limit maximum candidates
 * @param {boolean} [fuzzy] allow near-miss matches
 * @return {Array} ranked candidates
 */
function candidatesFromRows(rows, params, query, limit, fuzzy = false) {
	return resolveByFields({
		rows,
		normalizeRow: normalizeSystemBackup,
		query,
		limit,
		searchFields: params.search_fields,
		rank: rankLookupCandidates,
		fallbackValues: (candidate) => [candidate?.name],
		fuzzy,
	});
}

/**
 * Resolve System Backup candidates, paging past the first table page.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveSystemBackups(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (isRecentQuery(query)) {
		// "Recent" is newest Created At, not Refresh, and not a plain
		// lookup - see the identical comment in siteBackup/resolver.js.
		const rows = await loadRows(null, LOOKUP_FALLBACK_LIMIT);
		replaceResolvedEntities('system_backup', rows, systemBackupId);
		const newest = mostRecentRow(rows, 'created_at');
		return {candidates: newest ? [normalizeSystemBackup(newest)] : []};
	}
	if (!query && !browse) throw new Error('lookup_query_required');
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('system_backup', rows, systemBackupId);
		return {
			candidates: candidatesFromRows(rows, params, '', limit)
				.slice(0, limit),
		};
	}
	let rows = await loadRows(query, LOOKUP_LIMIT);
	let candidates = candidatesFromRows(rows, params, query, LOOKUP_LIMIT);
	// The list API already applied `search`. If the catalogue then drops
	// every row, keep the API hits instead of pretending nothing matched.
	if (!candidates.length && rows.length) {
		candidates = candidatesFromRows(rows, params, '', LOOKUP_LIMIT);
	}
	if (!candidates.length) {
		rows = await loadRows(null, LOOKUP_FALLBACK_LIMIT);
		candidates = candidatesFromRows(rows, params, query, LOOKUP_LIMIT);
		if (!candidates.length && params.fuzzy === true) {
			candidates = candidatesFromRows(
				rows, params, query, LOOKUP_LIMIT, true,
			);
		}
	}
	replaceResolvedEntities('system_backup', rows, systemBackupId);
	return {candidates};
}

/**
 * Return a retained System Backup row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedSystemBackup(entityId) {
	return getResolvedEntity('system_backup', entityId);
}
